import type { FooterBlock, StoreLink } from "../cms/footer";
import type { CMSData } from "../cms/models";

export interface StoreData {
  displayName?: string | null;
  cvr?: string | null;
  address?: {
    displayName?: string | null;
    streetName?: string | null;
    houseNumber?: string | null;
    zipCode?: string | null;
    cityName?: string | null;
    latitude?: number | string | null;
    longitude?: number | string | null;
  } | null;
  phone?: {
    internationalNumber?: string | null;
    prefix?: string | null;
    number?: string | null;
  } | null;
  email?: { address?: string | null } | null;
  openingHours?: Record<string, unknown> | null;
  soMes?: StoreSocialLink[] | null;
}
export interface StoreSocialLink {
  namespace?: string | null;
  url?: string | null;
  primary?: boolean | null;
}

export const STORE_FIELD_LABELS: Record<string, string> = {
  name: "Firmanavn",
  address: "Adresse",
  zipCity: "Postnr. og by",
  phone: "Telefon",
  email: "E-mail",
  cvr: "CVR",
};
export const STORE_FIELD_TOKENS: Record<string, string> = {
  name: "navn",
  address: "adresse",
  zipCity: "postnrBy",
  phone: "telefon",
  email: "email",
  cvr: "cvr",
};
const clean = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";
export function storeFields(store: StoreData | null | undefined) {
  return {
    name: clean(store?.displayName),
    address:
      [clean(store?.address?.streetName), clean(store?.address?.houseNumber)]
        .filter(Boolean)
        .join(" ") || clean(store?.address?.displayName).split(",")[0],
    zipCity: [clean(store?.address?.zipCode), clean(store?.address?.cityName)]
      .filter(Boolean)
      .join(" "),
    phone:
      clean(store?.phone?.internationalNumber) ||
      [clean(store?.phone?.prefix), clean(store?.phone?.number)]
        .filter(Boolean)
        .join(" "),
    email: clean(store?.email?.address),
    cvr: clean(store?.cvr),
  };
}
const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
/** `groupPhone` shows the phone grouped as the generation-2 contact card does (`unbrokenPhone`); the older components
 *  keep the stored form. */
export function storeText(
  html: string,
  store: StoreData | null | undefined,
  { groupPhone = false }: { groupPhone?: boolean } = {},
): string {
  const fields = {
    ...storeFields(store),
    ...(groupPhone ? { phone: unbrokenPhone(store?.phone) } : {}),
  };
  return html.replace(
    /<span\b[^>]*\bdata-reweb-store-field=["'](name|address|zipCity|phone|email|cvr)["'][^>]*>[\s\S]*?<\/span>/gi,
    (_span, key: string) =>
      escapeHtml(fields[key as keyof typeof fields] ?? ""),
  );
}
const DAYS = [
  ["monday", "Mandag"],
  ["tuesday", "Tirsdag"],
  ["wednesday", "Onsdag"],
  ["thursday", "Torsdag"],
  ["friday", "Fredag"],
  ["saturday", "Lørdag"],
  ["sunday", "Søndag"],
] as const;
export function openingRows(
  hours: Record<string, unknown> | null | undefined,
  options: {
    groupIdenticalDays?: boolean;
    hideClosedDays?: boolean;
    closedLabel?: string;
    layout?: string;
    ignoreHoursSwitch?: boolean;
  } = {},
) {
  if (!hours || (hours.hasOpeningHours === false && !options.ignoreHoursSwitch))
    return [];
  const rows: { label: string; value: string; first: number; last: number }[] =
    [];
  DAYS.forEach(([key, label], index) => {
    const raw = clean(hours[key]);
    if (!raw) return;
    const closed = /^(lukket|closed)$/i.test(raw);
    if (closed && options.hideClosedDays) return;
    const value = closed ? (options.closedLabel ?? "Lukket") : raw;
    const previous = rows.at(-1);
    if (
      (options.groupIdenticalDays || options.layout === "compact") &&
      previous &&
      previous.value === value &&
      previous.last === index - 1
    ) {
      previous.last = index;
      previous.label = `${DAYS[previous.first][1]}–${label}`;
    } else rows.push({ label, value, first: index, last: index });
  });
  return rows;
}
/** `groupedPhone` with non-breaking spaces, for a number inside running text that must not break over two lines. */
export function unbrokenPhone(phone: StoreData["phone"] | undefined): string {
  return groupedPhone(phone).replace(/ /g, "\u00a0");
}
/** The phone as PROD shows it: prefix and number (»+45 12 34 56 78«, other prefixes »+46 701234567«), else a Danish
 *  international number grouped the same way, else what is stored. */
export function groupedPhone(phone: StoreData["phone"] | undefined): string {
  const prefix = clean(phone?.prefix);
  const number = clean(phone?.number).replace(/\s+/g, "");
  if (prefix && number)
    return prefix === "+45"
      ? `${prefix} ${(number.match(/.{1,2}/g) ?? []).join(" ")}`
      : `${prefix} ${number}`;
  const international = clean(phone?.internationalNumber);
  const danish = /^\+45(\d{8})$/.exec(international);
  if (danish) return `+45 ${danish[1].match(/.{2}/g)!.join(" ")}`;
  return international || clean(phone?.number);
}
const SOCIAL_NETWORKS = [
  "facebook",
  "instagram",
  "linkedin",
  "x",
  "youtube",
  "tiktok",
  "email",
];
export const socialKey = (namespace: string) =>
  namespace.replace(/^some\./, "").replace(/^twitter$/, "x");
/** The store's social profiles that can be drawn (`some.www` never is), in the store's order or the order of
 *  `networks`. */
export function storeSocialLinks<T extends StoreSocialLink>(
  store: { soMes?: T[] | null } | null | undefined,
  networks?: string[],
): T[] {
  const available = (store?.soMes ?? []).filter(
    (item) =>
      item.namespace &&
      (SOCIAL_NETWORKS.includes(socialKey(item.namespace)) ||
        item.namespace === "some.trustpilot") &&
      safeStoreUrl(item.url),
  );
  return networks
    ? networks.flatMap((network) =>
        available.filter(
          (item) => socialKey(item.namespace!) === socialKey(network),
        ),
      )
    : available;
}
export function safeStoreUrl(value: unknown): string | null {
  const text = clean(value);
  if (
    !text ||
    Array.from(text).some((character) => character.charCodeAt(0) <= 32)
  )
    return null;
  if (text.startsWith("/") && !text.startsWith("//")) return text;
  if (/^(https?:|mailto:|tel:)/i.test(text)) return text;
  if (
    !/^[a-z][a-z\d+.-]*:/i.test(text) &&
    /^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(text)
  )
    return `https://${text}`;
  return null;
}
export function storePageLink(
  link: StoreLink,
  pages: CMSData["pages"],
): string | null {
  const page = link.pageId
    ? pages?.find(
        (p) =>
          p.id === link.pageId && p.type !== "footer" && p.type !== "article",
      )
    : undefined;
  if (page)
    return safeStoreUrl("/" + String(page.slug ?? "").replace(/^\/+/, ""));
  return safeStoreUrl(link.url);
}
export function privacyPagePath(pages: CMSData["pages"]): string | null {
  const page = pages?.find(
    (p) =>
      p.type !== "footer" &&
      p.sections?.some((section) => section.type === "PrivacyPolicySection"),
  );
  return page ? "/" + String(page.slug ?? "").replace(/^\/+/, "") : null;
}
export function configuredFooterBlocks(
  blocks: readonly FooterBlock[],
): FooterBlock[] {
  const known = blocks.filter((b) => b && typeof b === "object");
  const legal = known.find((b) => b.type === "LegalLinksComponent");
  const result = [...known];
  if (!legal)
    result.push({
      id: "system-legal",
      type: "LegalLinksComponent",
      enabled: true,
      ui: { column: 3 },
    });
  const logo = result.find(
    (b) => b.type === "CompanyLogoComponent" && b.enabled !== false,
  );
  return logo
    ? [
        { ...logo, ui: { ...logo.ui, column: 1 } },
        ...result.filter((b) => b !== logo),
      ]
    : result;
}
